import {
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
  forwardRef,
  useImperativeHandle
} from "react";
import { type SortingState } from "@tanstack/react-table";
import { useDebouncedValue } from "shared/hooks/use-debounced-value";
import { useSearchParamsState } from "shared/hooks/use-search-params-state";
import { useTasks } from "shared/app/TaskTableView/hooks/useTaskQuery";
import { mapTableSortingToApiSorts, TaskOptions } from "./constants";
import { TaskTableHeader } from "./components/TaskTableHeader";
import { TasksTable } from "./slices/TaskTable";

import { Pagination } from "shared/components/Pagination";
import { buildAssigneesParam } from "shared/components/Assignee";
import {
  TaskStatus,
  Task,
  GetTasksParams,
  SortDirection,
  relatedTo,
  Subject,
  TaskSubjectPayload
} from "./types";
import { TaskDrawer } from "./slices/TaskDrawer";
import { FiltersDrawer } from "./slices/FiltersDrawer";
import { NoRowsFallback } from "shared/components/DataGridTable/slices/NoRowsFallback";
import { CheckmarkSquare02Icon } from "@hugeicons-pro/core-stroke-rounded";

const DEFAULT_PER_PAGE = Number(window.localStorage.getItem("per_page")) || 10;

type OpenDialog = keyof typeof TaskOptions | null;

type TaskTableViewProps = {
  hasHeaderTitle?: boolean;
  showCompletedTasks: boolean;
  onShowCompletedTasksChange: (show: boolean) => void;
  onTaskDrawerClose?: () => void;
  relatedTo?: relatedTo;
  defaultSubject?: Subject;
};

export type TaskTableViewRef = {
  openCreateDrawer: () => void;
  openTaskDrawer: (taskId: number, noteId?: number) => void;
};

export const TaskTableView = forwardRef<TaskTableViewRef, TaskTableViewProps>(
  (
    {
      onTaskDrawerClose,
      hasHeaderTitle = false,
      showCompletedTasks = false,
      onShowCompletedTasksChange,
      relatedTo,
      defaultSubject
    },
    ref
  ) => {
    const handleShowCompletedTasksChange = (show: boolean) => {
      onShowCompletedTasksChange(show);
      resetToPageOne();
    };
    const [openDialog, setOpenDialog] = useState<OpenDialog>(null);
    const [viewingTask, setViewingTask] = useState<Task | null>(null);
    const [noteIdToScroll, setNoteIdToScroll] = useState<number | undefined>(
      undefined
    );
    const [sorting, setSorting] = useState<SortingState>([
      { id: "dueLabel", desc: false },
      { id: "createdAt", desc: true }
    ]);
    const [filters, setFilters] = useState<Partial<GetTasksParams>>({});
    const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<
      number[] | null
    >(null);

    useImperativeHandle(ref, () => ({
      openCreateDrawer: () => setOpenDialog(TaskOptions.create as OpenDialog),
      openTaskDrawer: (taskId: number, noteId?: number) => {
        setViewingTask({ id: taskId } as Task);
        setNoteIdToScroll(noteId);
        setOpenDialog(TaskOptions.view as OpenDialog);
      }
    }));
    const [searchParams, setSearchParams] = useSearchParamsState(
      {
        search: "",
        page: "1"
      },
      ["search", "page"]
    );

    const [debouncedSearch] = useDebouncedValue(searchParams.search, 500);
    const sorts = useMemo(() => {
      const userSorts = mapTableSortingToApiSorts(sorting);
      return [{ status: SortDirection.DESCENDING }, ...userSorts];
    }, [sorting]);
    const isLeadContext =
      relatedTo?.subject_type === TaskSubjectPayload.CRM_LEADS;
    const assigneesParam = buildAssigneesParam(selectedAssigneeIds) || [];

    const taskParams: GetTasksParams = {
      page: Number(searchParams.page) || 1,
      per_page: DEFAULT_PER_PAGE,
      search: debouncedSearch,
      statuses: showCompletedTasks
        ? [TaskStatus.INCOMPLETED, TaskStatus.COMPLETED]
        : [TaskStatus.INCOMPLETED],
      sorts,
      subjects: relatedTo?.subject_type
        ? [
            {
              subject_type: relatedTo.subject_type,
              subject_id: relatedTo.subject_id
            }
          ]
        : undefined,
      ...(assigneesParam.length > 0 && { assignees: assigneesParam }),
      ...filters
    };

    const {
      data: queryTasksData,
      refetch: refetchTasks,
      isFetching
    } = useTasks({ params: taskParams });
    const tasks = queryTasksData?.items ?? [];
    const pageCount = queryTasksData?.page_count ?? 0;
    const currentPage = Number(searchParams.page) || 1;

    const filterDrawer = openDialog === TaskOptions.filters;
    const createOpen = openDialog === TaskOptions.create;
    const viewTaskOpen = openDialog === TaskOptions.view;

    const resetToPageOne = (updates?: Partial<typeof searchParams>) => {
      setSearchParams({ ...searchParams, page: "1", ...updates });
    };
    const handleSortingChange: Dispatch<SetStateAction<SortingState>> = (
      newSorting
    ) => {
      if (typeof newSorting === "function")
        setSorting((prev) => newSorting(prev));
      else setSorting(newSorting);
    };
    const handleCreated = () => {
      refetchTasks();
      resetToPageOne();
    };

    const handleDeleteTask = () => {
      if (tasks.length === 1 && currentPage > 1)
        setSearchParams({ page: (currentPage - 1).toString() });
      else refetchTasks();
    };
    const handleAssigneeFilterChange = (userIds: number[] | null) => {
      setSelectedAssigneeIds(userIds);
      resetToPageOne();
    };

    return (
      <>
        <TaskTableHeader
          search={searchParams.search}
          setSearch={(search) => resetToPageOne({ search })}
          showCompletedTasks={showCompletedTasks}
          setShowCompletedTasks={handleShowCompletedTasksChange}
          onFiltersClick={() =>
            setOpenDialog(TaskOptions.filters as OpenDialog)
          }
          hideFilters={!!relatedTo}
          selectedAssigneeIds={selectedAssigneeIds}
          onSelectedAssigneeIdsChange={handleAssigneeFilterChange}
          isCreateTaskDrawerOpen={createOpen}
          setIsCreateTaskDrawerOpen={(isOpen) =>
            setOpenDialog(isOpen ? (TaskOptions.create as OpenDialog) : null)
          }
          onCreated={handleCreated}
          hasHeaderTitle={hasHeaderTitle}
        />

        <div className="w-full mx-auto">
          <TasksTable
            data={tasks}
            sorting={sorting}
            onSortingChange={(next) => {
              handleSortingChange(next);
              resetToPageOne();
            }}
            noRowsFallback={
              <NoRowsFallback
                Icon={CheckmarkSquare02Icon}
                text="There are no results right now. Try changing your filters or check back later"
                title="Nothing to show"
              />
            }
            isLoading={isFetching}
            onDeleted={handleDeleteTask}
            onTaskView={(task) => {
              setViewingTask(task);
              setOpenDialog(TaskOptions.view as OpenDialog);
            }}
            loadingTaskId={viewTaskOpen ? viewingTask?.id : null}
            showCompleted={showCompletedTasks}
            hideRelatedColumn={!!relatedTo}
            pagination={
              <div className="ml-auto flex md:max-w-[calc(100vw-106px)]">
                <Pagination
                  className="md:justify-end justify-center"
                  total={pageCount}
                  current={currentPage}
                  onChange={(page) =>
                    setSearchParams({ page: page.toString() })
                  }
                  disabled={isFetching}
                />
              </div>
            }
          />
        </div>

        <TaskDrawer
          open={createOpen}
          mode="create"
          onClose={() => setOpenDialog(null)}
          onCreated={handleCreated}
          defaultSubject={defaultSubject}
        />

        <TaskDrawer
          open={viewTaskOpen}
          mode="view"
          taskId={viewingTask?.id}
          noteId={noteIdToScroll}
          onClose={() => {
            setOpenDialog(null);
            setViewingTask(null);
            setNoteIdToScroll(undefined);
            onTaskDrawerClose?.();
          }}
          onCreated={handleCreated}
          readOnly={isLeadContext}
        />

        <FiltersDrawer
          open={filterDrawer}
          onClose={() => setOpenDialog(null)}
          onSave={(appliedFilters) => {
            setFilters(appliedFilters);
            resetToPageOne();
          }}
          filters={filters}
          taskParams={taskParams}
        />
      </>
    );
  }
);

TaskTableView.displayName = "TaskTableView";
